import React, { useEffect, useState } from 'react';
import { Paper, Typography, Button, List, ListItem, ListItemText, CircularProgress, Stack } from '@mui/material';
import { fetchGameHistory, saveGameRecord, deleteGameRecord } from '../services/historyService';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';

const formatDate = (dateLike) => {
  if (!dateLike) return '';
  if (typeof dateLike === 'string') return dateLike;
  if (dateLike.toDate) {
    return dateLike.toDate().toLocaleString();
  }
  return String(dateLike);
};

const HistoryPanel = ({ user, gameSnapshot, onLoadGame }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadHistory = async () => {
      if (!user) {
        setHistory([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchGameHistory(user.uid);
        if (isMounted) {
          setHistory(data);
        }
      } catch (err) {
        if (isMounted) {
          setError('履歴の取得に失敗しました');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHistory();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      await saveGameRecord(user.uid, gameSnapshot);
      const updated = await fetchGameHistory(user.uid);
      setHistory(updated);
    } catch (err) {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (gameId) => {
    if (!user || !window.confirm('この履歴を削除してもよろしいですか？')) return;
    try {
      await deleteGameRecord(user.uid, gameId);
      const updated = await fetchGameHistory(user.uid);
      setHistory(updated);
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography variant="h6">過去の試合履歴</Typography>
        {user && (
          <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : 'クラウドに保存'}
          </Button>
        )}
      </Stack>
      {!user && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          クラウド保存にはログインしてください
        </Typography>
      )}
      {user && loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">読み込み中...</Typography>
        </Stack>
      )}
      {user && !loading && history.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          保存済みの履歴はありません
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      {user && history.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {history.map((game) => (
            <ListItem
              key={game.id}
              divider
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ListItemText
                primary={`${game.pitcherName || '投手'} vs ${game.opponent || '相手'} / ${game.pitches || 0} 球`}
                secondary={formatDate(game.createdAt)}
                onClick={() => onLoadGame && onLoadGame(game)}
                sx={{ cursor: 'pointer' }}
              />
              <IconButton edge="end" aria-label="delete" onClick={(e) => {
                e.stopPropagation();
                handleDelete(game.id);
              }}>
                <DeleteIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default HistoryPanel;
